import { flushSync } from "react-dom";
import urlUtils from "@/common/utils/urlUtils";
import * as analytics from "@/modules/analytics";
import appController from "@/modules/appController";
import * as chainNav from "@/modules/chainNav";
import config from "@/modules/config";
import { valueNames } from "@/modules/Constants";
import * as dataAccess from "@/modules/dataAccess";
import { type DataAccessError } from "@/modules/dataAccess";
import type ExperienceData from "@/modules/ExperienceData";
import FavoritesData from "@/modules/FavoritesData";
import { ensureMixpanelInitialized } from "@/modules/mixpanelBootstrap";
import {
  installPrefetchListener,
  waitForParentPrefetch,
} from "@/modules/prefetchInbound";
import { isAdminPreviewSurface } from "@/modules/previewSurface";
import * as styleLoader from "@/modules/styleLoader";
import { ensureUmamiInitialized } from "@/modules/umami";
import { experienceStore, updateTemplateData } from "@/stores/experience/store";
import {
  experienceDataStatus,
  type ExperienceModel,
} from "@/stores/experience/types";

export function setExperienceData(experienceData: ExperienceData) {
  return experienceStore.dispatch(experienceData, handleSetExperienceData);
}

function handleSetExperienceData(
  state: ExperienceModel,
  experienceData: ExperienceData,
) {
  state.experienceData = experienceData;
  state.favoritesData = new FavoritesData(state.experienceData);
  state.plugin.configure(state);

  if (experienceData) {
    state.status = experienceDataStatus.LOADED;
    // The chain nav history infers the position from the id; the poster and
    // title feed the prev arrow's preview of a video the viewer already left.
    chainNav.recordCurrent(experienceData.idexperience, {
      thumbUrl: experienceData.getPosterImageUrl
        ? experienceData.getPosterImageUrl()
        : null,
      title: experienceData.title || "",
    });
    // A new experience starts un-ended, with no remaining time (so the chain
    // does not reveal before its first timeupdate) and the reveal latch reset.
    state.applicationData.setValue(valueNames.VIDEO_ENDED, false);
    state.applicationData.setValue(valueNames.VIDEO_REMAINING, null);
    state.applicationData.setValue(valueNames.CHAIN_REVEALED, false);
    if (!isAdminPreviewSurface()) {
      ensureMixpanelInitialized();
      analytics.flushMixpanelDeferred();
      ensureUmamiInitialized();
    }
    if (config.addFavorites()) {
      state.favoritesData.addAll();
    } else if (config.clearFavorites()) {
      state.favoritesData.removeAll();
    }
    updateTemplateData(state);
    styleLoader.loadStyles(document, experienceData);
  } else {
    state.status = experienceDataStatus.UNKNOWN;
  }
}

export function fetchExperienceData(
  region: string,
  id: unknown,
  experienceUrl: string | undefined,
) {
  // Metadata without an experienceUrl passes undefined on to the request.
  const url = experienceUrl as string;
  installPrefetchListener();

  const qs = urlUtils.querySettings();
  const hasConcreteExperienceId =
    id !== undefined &&
    id !== null &&
    String(id).trim() !== "" &&
    String(id) !== "null" &&
    String(id) !== "undefined";
  const expectParentPrefetch =
    hasConcreteExperienceId &&
    (qs["pix-embed-prefetch"] === "1" || qs["pix-embed-prefetch"] === "true");
  // The overlay parent posts the prefetch on iframe load and on initialized;
  // keep the wait short so the loading spinner does not linger.
  const PREFETCH_WAIT_MS = expectParentPrefetch ? 2500 : 150;

  waitForParentPrefetch(id, PREFETCH_WAIT_MS)
    .then(prefetchBody => {
      if (prefetchBody != null) {
        return dataAccess
          .createExperienceDataFromPayload(prefetchBody)
          .catch(() => dataAccess.getExperience(url));
      }
      return dataAccess.getExperience(url);
    })
    .then(experienceData => {
      const mediaWarmOnParent =
        qs["pix-media-warm"] === "1" || qs["pix-media-warm"] === "true";
      if (!mediaWarmOnParent) {
        experienceData.cacheArticleImages();
      }
      // Render the new experience now, so the player events it sends come before Experience Load.
      flushSync(() => appController.setExperienceData(experienceData));

      if (experienceData) {
        analytics.track("Experience Load", {
          pix_autospawn: config.isAutospawn(),
        });
      }
    })
    .catch((error: Partial<DataAccessError>) => {
      if (error.status != null) {
        analytics.track("Experience Load Error", {
          pix_autospawn: config.isAutospawn(),
          pix_idexperience: id,
          pix_status: error.status,
        });
        failure(`Error reading experience: ${id}.\n${error.errorString}`);
      } else {
        console.error(error);
      }
    });

  return experienceStore.dispatch(true, handleFetchExperienceData);
}

function handleFetchExperienceData(state: ExperienceModel) {
  state.error = null;
  state.status = experienceDataStatus.LOADING;
}

export function failure(error: string) {
  return experienceStore.dispatch(error, handleFailure);
}

function handleFailure(state: ExperienceModel, error: string) {
  console.warn(error);
  state.status = experienceDataStatus.ERROR;
  state.error = error;
}
